/* global search_algolia_vars */

import { liteClient } from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import { configure, hits, pagination, searchBox, stats } from 'instantsearch.js/es/widgets';
import { history } from 'instantsearch.js/es/lib/routers';
import { strl_searchHitTemplate } from './search-algolia-card';

const strl_searchMinChars = 3;
const strl_searchDebounceMs = 400;
const strl_searchUrlParam = '_search_search';

/**
 * Return empty Algolia result sets when the query is too short (avoids showing index browse results).
 *
 * @param {ReturnType<typeof liteClient>} baseClient
 */
function strl_searchClientRequireMinQuery(baseClient) {
	return {
		...baseClient,
		search(requests) {
			const blocked = requests.some((request) => {
				const q = typeof request.params?.query === 'string' ? request.params.query.trim() : '';
				return q.length < strl_searchMinChars;
			});

			if (blocked) {
				return Promise.resolve({
					results: requests.map((request) => ({
						hits: [],
						nbHits: 0,
						nbPages: 0,
						page: 0,
						hitsPerPage: request.params?.hitsPerPage ?? 12,
						processingTimeMS: 0,
						query: typeof request.params?.query === 'string' ? request.params.query : '',
						params: '',
						exhaustiveNbHits: true,
						exhaustiveTypo: true,
						renderingContent: {},
					})),
				});
			}

			return baseClient.search(requests);
		},
	};
}

/**
 * Algolia Highlight HTML may contain literal &amp; from WordPress-indexed strings; normalize for display.
 *
 * @param {unknown} field _highlightResult entry.
 * @returns {Record<string, unknown>|undefined}
 */
function strl_searchFixHighlightAmpersands(field) {
	if (!field || typeof field !== 'object' || typeof field.value !== 'string') {
		return undefined;
	}
	return {
		...field,
		value: field.value.replace(/&amp;/g, '&'),
	};
}

/**
 * @param {string} query
 * @param {function(string): void} runSearch
 */
function strl_searchQueryHook(query, runSearch) {
	const trimmed = typeof query === 'string' ? query.trim() : '';

	if (trimmed.length === 0) {
		runSearch('');
		return;
	}

	if (trimmed.length < strl_searchMinChars) {
		runSearch('');
		return;
	}

	window.clearTimeout(strl_searchQueryHook.timer);
	strl_searchQueryHook.timer = window.setTimeout(() => {
		runSearch(trimmed);
	}, strl_searchDebounceMs);
}
strl_searchQueryHook.timer = 0;

/**
 * Match FacetWP search markup so existing .facetwp-type-search styles apply.
 *
 * @param {string} searchLabel
 */
function strl_searchEnhanceSearchBoxMarkup(searchLabel) {
	const form = document.querySelector('#search-algolia-search .ais-SearchBox-form');
	if (!form || form.querySelector('.facetwp-input-wrap')) {
		return;
	}

	const wrap = document.createElement('div');
	wrap.className = 'facetwp-input-wrap';

	while (form.firstChild) {
		wrap.appendChild(form.firstChild);
	}

	wrap.querySelector('.ais-SearchBox-submit')?.remove();

	const input = wrap.querySelector('.ais-SearchBox-input');
	const reset = wrap.querySelector('.ais-SearchBox-reset');
	const glass = document.createElement('button');
	glass.type = 'button';
	glass.className = 'facetwp-search-button';
	glass.setAttribute('aria-hidden', 'true');
	glass.tabIndex = -1;

	if (input) {
		// type="text" avoids the browser's native clear icon (duplicate of Algolia reset).
		input.type = 'text';
		input.setAttribute('role', 'searchbox');
		input.setAttribute('enterkeyhint', 'search');
	}

	if (input && reset) {
		wrap.append(input, glass, reset);
	} else {
		wrap.appendChild(glass);
	}

	form.appendChild(wrap);
	strl_searchSyncResetVisibility();
}

/**
 * Hide clear button when the field is empty (InstantSearch sometimes leaves it visible).
 */
function strl_searchSyncResetVisibility() {
	const input = document.querySelector('#search-algolia-search .ais-SearchBox-input');
	const reset = document.querySelector('#search-algolia-search .ais-SearchBox-reset');
	if (!input || !reset) {
		return;
	}
	const empty = input.value.trim() === '';
	reset.hidden = empty;
	reset.classList.toggle('ais-SearchBox-reset--hidden', empty);
}

/**
 * @param {string} query
 */
function strl_searchUpdateHeading(query) {
	const heading = document.getElementById('search-algolia-heading');
	if (!heading) {
		return;
	}
	const label = heading.dataset.labelSearched || '';
	const labelEmpty = heading.dataset.labelEmpty || '';
	const t = typeof query === 'string' ? query.trim() : '';
	if (t.length >= strl_searchMinChars) {
		heading.textContent = `${label} ${t}`.trim();
	} else {
		heading.textContent = labelEmpty;
	}
}

/**
 * @param {string} query
 * @param {boolean} hasResults
 */
function strl_searchToggleResultsChrome(query, hasResults) {
	const active = typeof query === 'string' && query.trim().length >= strl_searchMinChars;

	document.querySelectorAll('[data-search-algolia-chrome="stats"]').forEach((el) => {
		el.classList.toggle('hidden', !active);
	});

	document.querySelectorAll('[data-search-algolia-chrome="pager"]').forEach((el) => {
		el.classList.toggle('hidden', !(active && hasResults));
	});
}

function strl_initSearchAlgolia() {
	const hitsRoot = document.getElementById('search-algolia-hits');
	if (!hitsRoot) {
		return;
	}

	const {
		algolia_app_id: appId,
		algolia_api_key: apiKey,
		index_name: indexName,
		no_hits: noHits,
		pagination_prev: prevLabel,
		pagination_next: nextLabel,
		stats_hits_text: statsHitsText,
		locale: statsLocale,
		search_placeholder: searchPlaceholder,
		category_lvl0_facet_attribute: categoryLvl0FacetAttribute,
	} = search_algolia_vars;

	if (!appId || !apiKey || !indexName) {
		hitsRoot.innerHTML = `<div class="col-span-4 text"><p class="mb-0">${noHits}</p></div>`;
		return;
	}

	const categoryLvl0Attr =
		typeof categoryLvl0FacetAttribute === 'string' && categoryLvl0FacetAttribute
			? categoryLvl0FacetAttribute
			: 'mention_categories.lvl0';

	const urlParams = new URLSearchParams(window.location.search);
	const initialQueryRaw = urlParams.get(strl_searchUrlParam) || '';
	const initialQuery = initialQueryRaw.trim().length >= strl_searchMinChars ? initialQueryRaw.trim() : '';

	const client = strl_searchClientRequireMinQuery(liteClient(appId, apiKey));

	const search = instantsearch({
		indexName,
		searchClient: client,
		routing: {
			router: history({
				createURL({ location, routeState }) {
					const { origin, pathname } = location;
					const params = new URLSearchParams(location.search);
					const q = routeState.query || '';
					if (q) {
						params.set(strl_searchUrlParam, q);
					} else {
						params.delete(strl_searchUrlParam);
					}
					const qs = params.toString();
					return `${origin}${pathname}${qs ? `?${qs}` : ''}`;
				},
				parseURL({ location }) {
					const params = new URLSearchParams(location.search);
					return {
						query: params.get(strl_searchUrlParam) || '',
					};
				},
			}),
			stateMapping: {
				stateToRoute(uiState) {
					const indexState = uiState[indexName] || {};
					return {
						query: indexState.query || '',
					};
				},
				routeToState(routeState) {
					const q =
						typeof routeState.query === 'string' && routeState.query.trim().length >= strl_searchMinChars
							? routeState.query.trim()
							: '';
					return {
						[indexName]: {
							query: q,
						},
					};
				},
			},
		},
		initialUiState: {
			[indexName]: {
				query: initialQuery,
			},
		},
		future: {
			preserveSharedStateOnUnmount: true,
		},
	});

	search.on('render', () => {
		const helper = search.helper;
		const uiQuery = helper?.state?.query || '';
		const queryActive = uiQuery.trim().length >= strl_searchMinChars;
		const hasResults = queryActive && (helper?.lastResults?.nbHits ?? 0) > 0;
		strl_searchUpdateHeading(uiQuery);
		strl_searchToggleResultsChrome(uiQuery, hasResults);
		strl_searchSyncResetVisibility();
		if (hitsRoot) {
			hitsRoot.classList.toggle('hidden', !queryActive);
		}
	});

	const paginationOptions = {
		showFirst: false,
		showLast: false,
		templates: {
			previous: `‹ ${prevLabel}`,
			next: `${nextLabel} ›`,
		},
		scrollTo: '#search-algolia-hits',
		cssClasses: {
			root: 'facetwp-type-pager',
			noRefinementRoot: 'hidden',
			list: 'facetwp-pager',
			item: 'facetwp-pager-item',
			link: 'facetwp-page',
			selectedItem: 'active',
			disabledItem: 'disabled',
		},
	};

	const configureParams = {
		hitsPerPage: 12,
	};

	const districtFilter =
		typeof search_algolia_vars.district_filter === 'string' ? search_algolia_vars.district_filter.trim() : '';
	if (districtFilter) {
		configureParams.filters = districtFilter;
	}

	search.addWidgets([
		configure({
			...configureParams,
		}),
		searchBox({
			container: '#search-algolia-search',
			placeholder: searchPlaceholder || '',
			queryHook: strl_searchQueryHook,
			showSubmit: false,
			showReset: true,
			showLoadingIndicator: false,
			cssClasses: {
				root: 'facetwp-type-search facetwp-facet facetwp-facet-search_search w-full',
				form: 'w-full',
				input: 'facetwp-search',
				reset: 'ais-SearchBox-reset',
				resetIcon: 'ais-SearchBox-resetIcon',
				submit: 'ais-SearchBox-submit hidden',
			},
		}),
		hits({
			container: '#search-algolia-hits',
			templates: {
				empty(results) {
					const q = typeof results.query === 'string' ? results.query.trim() : '';
					if (q.length < strl_searchMinChars) {
						return '';
					}
					return `<div class="col-span-4 text facetwp-template"><p class="mb-0">${noHits}</p></div>`;
				},
				item(hit, helpers) {
					return strl_searchHitTemplate(hit, helpers, search_algolia_vars);
				},
			},
			cssClasses: {
				root: 'w-full',
				list: 'gap-2 grid grid-cols-1 md:grid-cols-4 facetwp-template list-none p-0 m-0',
				item: 'flex h-full min-h-0 min-w-0',
				emptyRoot: 'col-span-4',
			},
			transformItems(items, { results }) {
				const q = typeof results?.query === 'string' ? results.query.trim() : '';
				if (q.length < strl_searchMinChars) {
					return [];
				}
				return items.map((item) => {
					const hr = item._highlightResult || {};
					const postTitleHr = strl_searchFixHighlightAmpersands(hr.post_title);
					const subtitleHr = strl_searchFixHighlightAmpersands(hr['mention-subtitle']);
					if (!postTitleHr && !subtitleHr) {
						return item;
					}
					return {
						...item,
						_highlightResult: {
							...hr,
							...(postTitleHr && { post_title: postTitleHr }),
							...(subtitleHr && { 'mention-subtitle': subtitleHr }),
						},
					};
				});
			},
		}),
		stats({
			container: '#search-algolia-stats',
			templates: {
				text(data, { html }) {
					const raw = statsHitsText || '%s tips for your kids';
					const n = data.nbHits.toLocaleString(statsLocale || undefined);
					const sentence = raw.split('%s').join(n);
					return html`<span
						class="facetwp-facet facetwp-facet-results_amount facetwp-type-pager text-[13px] text-black"
						>${sentence}</span
					>`;
				},
			},
		}),
		pagination({
			container: '#search-algolia-pagination-top',
			...paginationOptions,
		}),
		pagination({
			container: '#search-algolia-pagination-bottom',
			...paginationOptions,
		}),
	]);

	search.start();

	strl_searchEnhanceSearchBoxMarkup(searchPlaceholder || '');

	document
		.querySelector('#search-algolia-search .ais-SearchBox-input')
		?.addEventListener('input', strl_searchSyncResetVisibility);

	strl_searchUpdateHeading(initialQuery);
	strl_searchToggleResultsChrome(initialQuery, false);
}

document.addEventListener('DOMContentLoaded', strl_initSearchAlgolia);
